import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MONTHS, DOW, daysInMonth, firstDayIndex, isoDate, londonToday, dateFullyInsideNoticeWindow, isPastDate } from '../../lib/bookingTime';

export default function BookingCalendar({ selectedDate, onSelect }) {
  const today = useMemo(() => londonToday(), []);
  const [view, setView] = useState({ y: today.y, m: today.m });

  const totalDays = daysInMonth(view.y, view.m);
  const startIdx = firstDayIndex(view.y, view.m);

  const cells = [];
  for (let i = 0; i < startIdx; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const goPrev = () => {
    const canGoBack = !(view.y === today.y && view.m === today.m);
    if (!canGoBack) return;
    const m = view.m - 1;
    if (m < 0) setView({ y: view.y - 1, m: 11 });
    else setView({ y: view.y, m });
  };
  const goNext = () => {
    const m = view.m + 1;
    if (m > 11) setView({ y: view.y + 1, m: 0 });
    else setView({ y: view.y, m });
  };

  const isDisabled = (d) => {
    if (!d) return true;
    const ds = isoDate(view.y, view.m, d);
    if (isPastDate(ds)) return true;
    if (dateFullyInsideNoticeWindow(ds)) return true;
    return false;
  };

  const isSelected = (d) => selectedDate && d && selectedDate === isoDate(view.y, view.m, d);
  const isToday = (d) => d && view.y === today.y && view.m === today.m && d === today.d;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-2xl text-[#2E2C28] font-light">
          {MONTHS[view.m]} <span className="text-[#B5876C] italic">{view.y}</span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            aria-label="Previous month"
            disabled={view.y === today.y && view.m === today.m}
            className="w-10 h-10 border border-[#B5876C]/40 hover:bg-[#B5876C] hover:text-[#EBE4DE] disabled:opacity-30 disabled:cursor-not-allowed text-[#2E2C28] flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={goNext}
            aria-label="Next month"
            className="w-10 h-10 border border-[#B5876C]/40 hover:bg-[#B5876C] hover:text-[#EBE4DE] text-[#2E2C28] flex items-center justify-center transition-colors"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {DOW.map(d => (
          <div key={d} className="text-[10px] tracking-[0.24em] uppercase text-[#7a7369] text-center py-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const disabled = isDisabled(d);
          const sel = isSelected(d);
          const td = isToday(d);
          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => !disabled && onSelect(isoDate(view.y, view.m, d))}
              data-testid={`cal-day-${d}`}
              className={[
                'aspect-square flex items-center justify-center text-sm font-light transition-colors relative',
                disabled ? 'text-[#c0b8ac] cursor-not-allowed' : 'text-[#2E2C28] hover:bg-[#B5876C]/15 cursor-pointer',
                sel ? '!bg-[#B5876C] !text-[#EBE4DE] font-medium' : '',
              ].join(' ')}
            >
              {d}
              {td && !sel && <span className="absolute bottom-1.5 w-1 h-1 rounded-full bg-[#B5876C]" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
